// components/RepliesPage/dialogs/ProcessTweetModal.tsx
import {
  MessageCircle,
  CheckCircle,
  LoaderCircle,
  Link as LinkIcon,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ProcessTweetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProcess: (tweetId: string) => Promise<void>;
  tweetId: string;
  onChange: (tweetId: string) => void;
  isProcessing: boolean;
}

const ProcessTweetModal: React.FC<ProcessTweetModalProps> = ({
  isOpen,
  onClose,
  onProcess,
  tweetId,
  onChange,
  isProcessing,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset Error on Modal Open
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  //  Extract ID from URL
  const extractIdFromInput = (input: string): string => {
    const statusMatch = input.match(/status\/(\d+)/i);
    if (statusMatch && statusMatch[1]) {
      return statusMatch[1];
    }
    return input;
  };

  // Validate tweet ID format
  const validateTweetId = (id: string): boolean => {
    const numericRegex = /^\d{15,20}$/;
    const urlRegex = /twitter\.com|x\.com|status/i;

    if (urlRegex.test(id)) {
      setValidationError("Please enter only the numeric ID, not the full URL");
      return false;
    }

    if (!numericRegex.test(id)) {
      setValidationError("Please enter a valid Tweet ID.");
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleInputChange = (value: string) => {
    onChange(value);

    if (value.trim()) {
      validateTweetId(value.trim());
    } else {
      setValidationError(null);
    }
  };

  const handleProcess = async () => {
    setError(null);

    if (!validateTweetId(tweetId)) {
      return;
    }

    try {
      await onProcess(tweetId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to process tweet. Please try again.",
      );
    }
  };

  //  If user pastes a URL, extract the ID
  const handlePasteInput = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");
    const urlRegex = /twitter\.com|x\.com|status/i;

    if (urlRegex.test(pastedText)) {
      e.preventDefault();
      const extractedId = extractIdFromInput(pastedText);
      onChange(extractedId);
      validateTweetId(extractedId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Reply to Specific Tweet
          </DialogTitle>
          <DialogDescription>
            Generate and process a response to a specific Twitter mention
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Enter the ID of a specific tweet that mentions your account. Your AI
            agent will generate a response which will either be automatically
            posted (if automated replies are enabled) or added to your pending
            queue for approval.
          </p>

          <div className="bg-muted p-3 rounded-md text-sm">
            <h4 className="font-medium mb-1 flex items-center">
              <LinkIcon className="h-4 w-4 mr-1 text-blue-500" />
              How to find a Tweet ID:
            </h4>
            <ol className="list-decimal pl-5 text-muted-foreground mt-1 space-y-1">
              <li>Navigate to the tweet on Twitter</li>
              <li>Click on the share button</li>
              <li>Select "Copy link to Tweet"</li>
              <li>The Tweet ID is the number at the end of the URL</li>
            </ol>

            <div className="mt-3 bg-card/50 p-2 rounded border border-dashed text-xs font-mono">
              https://twitter.com/username/status/
              <span className="text-blue-500 font-semibold">
                1489012345678901234
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tweet-id">Tweet ID</Label>
            <Input
              id="tweet-id"
              placeholder="e.g., 1489012345678901234"
              value={tweetId}
              onChange={(e) => handleInputChange(e.target.value)}
              onPaste={handlePasteInput}
              className={validationError ? "border-red-500" : ""}
            />
            {validationError && (
              <p className="text-sm text-red-500 mt-1 flex items-start gap-1">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{validationError}</span>
              </p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4 mr-2" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !tweetId.trim() || !!validationError}
          >
            {isProcessing ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Process Tweet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessTweetModal;
